import { ReactNode } from "react";

import { Box, Chip, Typography } from "@mui/material";
import { differenceInDays, differenceInMinutes, format } from "date-fns";

import { AppointmentResponse, SystemDropdownCategory } from "@/api";
import { isOverbookablePurpose } from "@/common/components/appointments/shared/AppointmentForm.utils";
import { ClientLink } from "@/common/components/client-link/ClientLink";
import { EllipsisText } from "@/common/components/ellipsis-text/EllipsisText";
import { RowActionButton } from "@/common/components/row-action-button/RowActionButton";
import { useAppointmentLabels } from "@/common/hooks/useAppointmentLabels";
import { useSystemDropdownOptions } from "@/common/hooks/useSystemDropdownOptions";
import { useTranslation } from "@/translations";

interface Props {
  appointment: AppointmentResponse;
  onOpen: () => void;
}

// Shorter appointments are too low for the stacked layout; tied to the .fc-timegrid-slot height in Calendar.css.
const SHORT_APPOINTMENT_THRESHOLD_MINUTES = 30;

export function AppointmentWeekWidget({ appointment, onOpen }: Props) {
  const { t } = useTranslation();
  const { purposeLabelOf } = useAppointmentLabels();
  const purposes = useSystemDropdownOptions(SystemDropdownCategory.AppointmentPurpose);
  const purposeLabel = purposeLabelOf(appointment.purposeKey);
  // "Nachname, Vorname", as every other client list in the app renders it — and driven by the last
  // name, which is the half that is always stored, so a client without a first name still shows.
  const clientName = appointment.clientLastName
    ? [appointment.clientLastName, appointment.clientFirstName].filter(Boolean).join(", ")
    : (appointment.clientFirstName ?? null);
  const durationMinutes = differenceInMinutes(appointment.endTimeUtc, appointment.startTimeUtc);
  const isShort = !appointment.isAllDay && durationMinutes < SHORT_APPOINTMENT_THRESHOLD_MINUTES;
  const timeRange = `${format(appointment.startTimeUtc, "HH:mm")}–${format(appointment.endTimeUtc, "HH:mm")}`;

  // Overbookable slots carry an availability chip: FREI while no client is booked, BELEGT once
  // one is. Non-overbookable purposes show no chip.
  const showChip = isOverbookablePurpose(appointment.purposeKey, purposes);
  const occupied = !!appointment.clientId;
  const chip = showChip ? (
    <Chip
      size="small"
      // The palette comes from the theme's chip colors: success (green) for a free slot,
      // info (light blue) for a booked one.
      color={occupied ? "info" : "success"}
      label={occupied ? t("calendar.appointmentChip.occupied") : t("calendar.appointmentChip.free")}
      sx={{
        position: "absolute",
        // Above the RowActionButton, which is positioned too and comes later, so it would paint over the chip.
        zIndex: 1,
        top: 2,
        right: 2,
        height: 16,
        fontSize: "0.625rem",
        fontWeight: 700,
        pointerEvents: "none",
        "& .MuiChip-label": { px: 0.75 },
      }}
    />
  ) : null;
  // Clears the wider chip (BELEGT, ~56px plus its 2px offset), so truncated text ends before it, not under it.
  const chipClearance = showChip ? 7.5 : 0;
  const contentPaddingRight = 0.5 + chipClearance;

  const clientLink =
    appointment.clientId && clientName ? (
      <EllipsisText value={clientName} variant="caption">
        <ClientLink clientId={appointment.clientId} name={clientName} variant="caption" />
      </EllipsisText>
    ) : null;
  // The one-line layouts can't fold the name into the label text: as a link it must stay outside the
  // RowActionButton. Label and name shrink in proportion to their widths, so both stay partly visible;
  // a label that never shrank would squeeze the link to zero width, leaving it unclickable yet focusable.
  const inlineLabel = (label: ReactNode) => (
    <Box minWidth={0}>
      <RowActionButton onClick={onOpen}>{label}</RowActionButton>
    </Box>
  );
  const inlineClientLink = clientLink && (
    <>
      <Typography variant="caption" color="inherit" flexShrink={0} aria-hidden="true">
        ·
      </Typography>
      <Box minWidth={0}>{clientLink}</Box>
    </>
  );

  if (appointment.isAllDay) {
    const days = differenceInDays(appointment.endTimeUtc, appointment.startTimeUtc);
    return (
      <Box position="relative" height="100%">
        {chip}
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          gap={0.5}
          pl={0.5}
          pr={contentPaddingRight}
          py={0}
          maxWidth="100%"
          overflow="hidden"
        >
          <Typography variant="caption" color="inherit" noWrap flexShrink={0}>
            {t("calendar.allDayDays", { count: days })}
          </Typography>
          {inlineLabel(<EllipsisText value={purposeLabel} variant="caption" fontWeight="bold" />)}
          {inlineClientLink}
        </Box>
      </Box>
    );
  }

  return (
    <Box position="relative" height="100%">
      {chip}
      {isShort ? (
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          gap={0.5}
          pl={0.5}
          pr={contentPaddingRight}
          py={0}
          maxWidth="100%"
          overflow="hidden"
        >
          {inlineLabel(<EllipsisText value={`${timeRange} ${purposeLabel}`} variant="caption" />)}
          {inlineClientLink}
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" px={0.5} py={0.25} maxWidth="100%">
          <Box pr={chipClearance}>
            <RowActionButton onClick={onOpen}>
              <EllipsisText value={purposeLabel} variant="caption" fontWeight="bold" />
            </RowActionButton>
          </Box>
          <Box display="flex" flexDirection="row" alignItems="center" gap={0.5} minWidth={0}>
            <Typography variant="caption" color="inherit" noWrap flexShrink={0}>
              {timeRange}
            </Typography>
            {inlineClientLink}
          </Box>
        </Box>
      )}
    </Box>
  );
}
